import { v4 as uuidv4 } from "uuid";

import MediaFileModel from "../../../models/MediaFile";
import Broadcaster from "../../../helpers/Broadcaster";
import { setTiptapSidemenu } from "../../../store/app";

// Utility for normalizing file names
export function escapeFilename(filename) {
  return filename
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[^0-9A-Za-z.\-]/g, "_")
    .slice(0, 255);
}

// Get file extension
export function getExtension(path) {
  const baseName = path.split(/[\\/]/).pop();
  const pos = baseName.lastIndexOf(".");
  return (baseName === "" || pos < 1) ? "" : baseName.slice(pos + 1).toLowerCase();
}

// Determine file type by extension
export function getEmbedLinkType(link) {
  const ext = getExtension(link);
  const typeMap = {
    "pdf,doc,docx,xls,xlsx,ppt,pptx": "file",
    "png,jpg,jpeg,gif": "image",
    "mp3,ogg,mpeg": "audio",
    "video": "mp4,mov,mkv,yuv"
  };
  for (const [type, extensions] of Object.entries(typeMap)) {
    if (extensions.includes(ext)) {
      return type;
    }
  }
  return "image";
}

// Create a media file record
export function createMediaFile(id, url, type = "file") {
  const mediaFile = {
    original_url: `https://sutra-froala.s3.us-west-1.amazonaws.com/uploads/${id}`,
    url: url,
    upload_type: type,
    slug: window.$currentSlug,
  };
  MediaFileModel.onCreate(
    mediaFile,
    () => console.log("Media file stored"),
    () => {}
  );
}

// Add Unsplash attribution
export function addUnsplashAttribution(editor, imageData) {
  const unsplashBaseUrl = "https://unsplash.com/";
  const userLink = imageData?.user?.links?.html || unsplashBaseUrl;
  editor.chain().addNewEmptyLine().insertContent({
    type: 'paragraph',
    content: [
      { type: 'text', text: "Photo by " },
      {
        type: 'text',
        text: `${imageData?.user?.first_name} ${imageData?.user?.last_name || ""}`,
        marks: [{
          type: "link",
          attrs: {
            target: "_blank",
            href: `${userLink}?utm_source=Sutra&utm_medium=referral`,
            rel: "noopener noreferrer nofollow"
          }
        }]
      },
      { type: 'text', text: ` on ` },
      {
        type: 'text',
        text: 'Unsplash',
        marks: [{
          type: "link",
          attrs: {
            target: "_blank",
            href: `${unsplashBaseUrl}?utm_source=Sutra&utm_medium=referral`,
            rel: "noopener noreferrer nofollow"
          }
        }]
      }
    ]
  }).run();
}

// Trigger Unsplash download (for statistics)
export function triggerUnsplashDownload(photoId) {
  if (!photoId) return;
  fetch(`https://api.unsplash.com/photos/${photoId}/download?client_id=4BHv6-IGLP-oa0rhTeyUnT40xuClCYynK9e0ZwWjpbA`)
    .catch((error) => console.error("Unsplash download error:", error));
}

// Universal function for inserting a file into the editor
export function createNode({
  type,
  url,
  editor,
  dispatch,
  extras = {},
  notMain = false,
  onSave = () => {},
  action = undefined
}) {
  if (action === "update") {
    editor.commands.updateAttributes(type, { src: url, ...extras });
  } else {
    const commonAttributes = {
      ...extras,
      textAlign: !notMain ? "center" : "left",
      radius: "with-radius",
      resizeableWidth: "100%",
      space_id: window.$currentSlug
    };
    switch (type) {
      case "image":
        editor.chain().addNewEmptyLine().setImage({ src: url, ...commonAttributes }).run();
        break;
      case "unsplash":
        const unsplashUrl = url?.urls?.regular;
        editor.chain().addNewEmptyLine().setImage({
          src: unsplashUrl,
          ...commonAttributes,
          link: { url: unsplashUrl, target: "_blank" }
        }).run();
        addUnsplashAttribution(editor, url);
        triggerUnsplashDownload(url?.id);
        break;
      case "video":
        editor.chain().addNewEmptyLine().setVideo({ src: url, ...commonAttributes }).run();
        break;
      case "audio":
        editor.chain().addNewEmptyLine().setAudio({ src: url, ...commonAttributes }).run();
        break;
      case "file":
        editor.chain().addNewEmptyLine().setFile({
          src: url,
          ...extras,
          textAlign: "left",
          displayAs: "link"
        }).run();
        break;
      default:
        editor.chain().addNewEmptyLine().setFile({
          src: url,
          ...commonAttributes,
          displayAs: "link"
        }).run();
    }
  }
  if (!notMain && dispatch) {
    window.$tiptapLastSelectionRange = {
      from: editor.view.state.selection.from,
      to: editor.view.state.selection.to
    };
    // dispatch(setTiptapSidemenu({ opened: "configuration" }));
  }
  if (onSave) onSave();
}

// Determine node type by file mime-type
export function getNodeTypeByFile(file) {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return 'file';
}

// Universal function for uploading a file (image, video, audio, file)
export function handleFileUpload({
  file,
  editor,
  dispatch,
  uploadType,
  space,
  notMain,
  onSave
}) {
  let extension = file.name.split(".").pop()?.toLowerCase() || "";
  const fileName = escapeFilename(file.name).replace(`.${extension}`, "");
  let id, blockUrl;

  if (uploadType === "video") {
    const baseName = `${encodeURIComponent(uuidv4().replace(/-/g, "").replace(/_/g, ""))}_${window.$videoUploadKey}`;
    id = `${uuidv4().replace(/-/g, "")}/${baseName}.${extension}`;
    blockUrl = `https://sutra-video-output.s3.us-west-1.amazonaws.com/${baseName}.m3u8`;
  } else {
    id = `${uuidv4().replace(/-/g, "")}/${encodeURIComponent(fileName)}.${extension}`;
    blockUrl = null;
  }

  const extras = {};
  if (uploadType === "file") {
    extras.name = file.name;
    extras.size = file.size;
    extras.extension = extension;
  }

  createNode({
    type: uploadType,
    url: blockUrl,
    editor,
    dispatch,
    extras: {
      ...extras,
      uploader: {
        id: id,
        status: "pending",
        progress: 0,
        url: blockUrl,
        urlOutput: blockUrl,
        space_slug: window.$currentSlug
      },
    },
    notMain,
    onSave
  });

  createMediaFile(id, blockUrl || `https://sutra-froala.s3.us-west-1.amazonaws.com/uploads/${id}`, uploadType);

  Broadcaster.send("new_uploadFile", document.body, {
    id: id,
    file: file,
    editor: editor,
    type: uploadType,
    status: "pending",
    space: space,
    trigger: "fileHandler",
    url: blockUrl,
    extension: extension,
    space_slug: window.$currentSlug
  });
}

// Handle file drop for FileHandler
export function handleFileDrop(currentEditor, files, pos, options = {}) {
  files.forEach(file => {
    const uploadType = getNodeTypeByFile(file);
    handleFileUpload({
      file,
      editor: currentEditor,
      dispatch: options.dispatch,
      uploadType,
      space: options.space || {}
    });
  });
}

// Handle file paste for FileHandler
export function handleFilePaste(currentEditor, files, htmlContent, options = {}) {
  files.forEach(file => {
    if (htmlContent) return false;
    const uploadType = getNodeTypeByFile(file);
    handleFileUpload({
      file,
      editor: currentEditor,
      dispatch: options.dispatch,
      uploadType,
      space: options.space || {}
    });
  });
} 